import { useEffect, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { ErrorCode } from 'react-dropzone';
import { useParams } from 'react-router-dom';
import { toast as sonnerToast } from 'sonner';

import { FileWithPreview, createFilePreview } from './utils';
import { APIBaseChronos } from '../../../api/hosts';
import { useGetSubset } from '../../../api/queries/useGetSubset';
import useImportRelativityList from '@/api/mutations/useImportRelativityList';
import { useGetApideckAllConnections } from '@/api/queries/useGetApideckAllConnections';
import { useGetApideckSession } from '@/api/queries/useGetApideckSession';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { RelativityWorkspaceType, RelativityListType } from '@/api/types/backend-dto-types';
import { ApideckConnection } from '@/types';
import { BroadcastChannelNames, RelativityAuthMessageTypes, RelativityAuthMessages } from '@/types/broadcast-channels';

interface UseFileUploadLogicProps {
  subsetId?: string | null;
  isApideckEnabled?: boolean;
  maxFiles?: number;
  onApideckSuccess?: () => void;
}

// For now we consider a upload to the matter to be a upload to the 'All Documents' subset of the
// matter in order to make the migration simpler. We'll likely remove the case <-> file relation
// in the future however.
export const useFileUploadLogic = ({
  subsetId,
  isApideckEnabled,
  maxFiles,
  onApideckSuccess,
}: UseFileUploadLogicProps) => {
  // File state
  const { matterId } = useParams();

  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [localError, setLocalError] = useState<ErrorCode | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(null);
  const [isRelativityModalOpen, setIsRelativityModalOpen] = useState(false);

  // Apideck state
  const [apiDeckSessionToken, setApiDeckSessionToken] = useState<string | null>(null);
  const [apiDeckConnections, setApiDeckConnections] = useState<Array<ApideckConnection>>([]);

  // Relativity auth state
  const authInfo = useAuthInfo();
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const sessionKeyRef = useRef<string | null>(null);

  // API hooks
  const { data: apideckSessionData, refetch: refetchApideckSession } = useGetApideckSession({
    enabled: false,
    refetchOnWindowFocus: false,
  });
  const { refetch: refetchConnections } = useGetApideckAllConnections({
    enabled: false,
    refetchOnWindowFocus: false,
  });
  const { data: matter } = useGetMatterV2(matterId);
  const { refetch: isPendingUploadRefetch } = useGetSubset(matterId, matter?.allDocumentsCaseId ?? undefined);

  // Relativity hooks
  const importMutation = useImportRelativityList();

  // Relativity broadcast channel setup
  useEffect(() => {
    const channel = new BroadcastChannel(BroadcastChannelNames.RelativityAuth);
    broadcastChannelRef.current = channel;

    const handleBroadcastMessage = (event: MessageEvent<RelativityAuthMessages>) => {
      if (!sessionKeyRef.current || event.data.sessionKey !== sessionKeyRef.current) {
        return;
      }

      sessionKeyRef.current = null;

      if (event.data.type === RelativityAuthMessageTypes.Success) {
        setIsRelativityModalOpen(true);
      } else if (event.data.type === RelativityAuthMessageTypes.Error) {
        sonnerToast.error(`Failed to connect to Relativity: ${event.data.description || event.data.error}`);
      }
    };

    channel.addEventListener('message', handleBroadcastMessage);

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, []);

  // Apideck session setup
  const refetchApideckSessionFunc = async () => {
    const { data } = await refetchApideckSession();
    if (data) setApiDeckSessionToken(data.sessionToken);
  };

  useEffect(() => {
    if (!isApideckEnabled) return;
    if (apideckSessionData?.sessionToken) {
      setApiDeckSessionToken(apideckSessionData.sessionToken);
    } else {
      refetchApideckSessionFunc();
    }
  }, [apideckSessionData, isApideckEnabled]);

  // Apideck connections setup
  const resetConnections = async () => {
    const { data } = await refetchConnections();
    if (data) setApiDeckConnections(data);
  };

  useEffect(() => {
    if (apiDeckSessionToken) {
      resetConnections();
    }
  }, [apiDeckSessionToken]);

  // File handling
  const handleFileAccepted = (acceptedFiles: File[], onDrop: (files: File[]) => void) => {
    setLocalError(null);

    if (maxFiles === files.length) {
      return setLocalError(ErrorCode.TooManyFiles);
    }

    const newFiles = acceptedFiles.map(createFilePreview);
    setFiles([...files, ...newFiles]);
    onDrop(acceptedFiles);
  };

  const onDeleteFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  // Upload validation
  const isPendingUpload = async (): Promise<boolean> => {
    const { data } = await isPendingUploadRefetch();
    return data?.isPendingUpload === true;
  };

  // Relativity integration
  const connectRelativity = async () => {
    try {
      const statusResponse = await fetch(`${APIBaseChronos}/oauth/relativity/status`, {
        headers: {
          Authorization: `Bearer ${await authInfo.tokens.getAccessToken()}`,
        },
      });

      if (!statusResponse.ok) {
        throw new Error('Failed to check Relativity auth status');
      }

      const statusData = await statusResponse.json();
      if (statusData.isAuthenticated === true) {
        setIsRelativityModalOpen(true);
        return;
      }

      const response = await fetch(`${APIBaseChronos}/oauth/relativity/start`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await authInfo.tokens.getAccessToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to start OAuth flow');
      }

      const data = await response.json();
      const url = new URL(data.authUrl);
      const sessionKey = url.searchParams.get('state');

      if (sessionKey) {
        sessionKeyRef.current = sessionKey;
      }

      window.open(data.authUrl, 'relativity-auth', 'width=800,height=600');
    } catch (error) {
      console.error('Error starting Relativity OAuth:', error);
      sonnerToast.error('Failed to start Relativity integration');
    }
  };

  // Modal handling
  const handleOpenApideckModal = async (connection: string, modalContent: React.ReactNode) => {
    if (!apiDeckSessionToken) return;

    if (await isPendingUpload()) {
      sonnerToast.info('An upload is still in progress. Only one concurrent upload is allowed.', {
        duration: 3000,
        position: 'top-right',
        richColors: true,
      });
    } else {
      setModalContent(modalContent);
      setModalOpen(true);
    }
  };

  // Relativity modal handling
  const handleSelectRelativityList = async (workspace: RelativityWorkspaceType, list: RelativityListType) => {
    if (!subsetId) return;

    try {
      await importMutation.mutateAsync({
        workspaceId: workspace.artifactId,
        listId: list.artifactId,
        caseId: subsetId ?? '',
        listName: list.name,
      });

      sonnerToast.success(`Starting import of ${list.name} from ${workspace.name}`);
    } catch (error) {
      console.error('Failed to start import:', error);
      sonnerToast.error('Failed to start Relativity import');
    }
  };

  return {
    // File state
    files,
    localError,
    setLocalError,

    // File handlers
    handleFileAccepted,
    onDeleteFile,

    // Modal state
    modalOpen,
    setModalOpen,
    modalContent,
    isRelativityModalOpen,
    setIsRelativityModalOpen,

    // Apideck state
    apiDeckSessionToken,
    apiDeckConnections,

    // Integration handlers
    connectRelativity,
    handleOpenApideckModal,
    handleSelectRelativityList,
    isPendingUpload,
  };
};
